import MyListIcon from "@DSG/Icons/MyListIcon";
import colorPallete, { elevation, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import MyListsEditListForm from "@DSG/Overrides/Widgets/MyLists/MyListsEditListForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import WishListCard, { WishListCardStyles } from "@insite/content-library/Components/WishListCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TimeToLoad from "@DSG/Icons/TimeToLoad";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

interface State {
    createListModalIsOpen: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    cardList?: InjectableCss;
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    cardTitleWrapper?: InjectableCss;
    myListTitle?: TypographyProps;
    viewAllLink?: TypographyProps;
    cardwrapper?: InjectableCss;
    createListModal?: ModalPresentationProps;
    buttonWrapper?: InjectableCss;
    createNewListButton?: InjectableCss;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },
    },
    cardTitleWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 8px;
            align-items: center;
            margin-top: 30px;
            padding-bottom: 15px;
            > div {
                display: flex;
                justify-content: space-between;
                max-width: 100%;
                flex-basis: 100%;
                padding: 0;
                align-items: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            gap: 16px;
                        `,
                        css`
                            margin-top: 40px;
                            padding-bottom: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    myListTitle: {
        css: css`
            margin: 0;
            font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize} /
                ${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    viewAllLink: {
        css: css`
            color: ${colorPallete.primaryRed500};
            font-weight: 600;
            text-decoration: underline;
            [class*="TypographyStyle"] {
                color: ${colorPallete.primaryRed500};
                text-decoration: underline;
                font-size: 14px;
                &:hover {
                    color: ${colorPallete.primaryMaroon500};
                }
            }
        `,
    },
    cardList: {
        css: css`
            border-radius: 8px;
            border: 1px solid ${colorPallete.grey500};
            box-shadow: ${elevation.dp04};
            padding: 16px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            [data-test-selector="myLists_PrivateLabel"] {
                                [class*="TooltipContainer"] {
                                    transform: translateX(10%);
                                    left: auto;
                                    right: 0;
                                }
                                [class*="TooltipArrow"] {
                                    transform: initial;
                                    left: auto;
                                    right: 28px;
                                }
                            }
                            .myListsVisibility {
                                [class*="TooltipContainer"] {
                                    left: -14px;
                                }
                                [class*="TooltipArrow"] {
                                    right: 70px;
                                    left: auto;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    cardwrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.lessThanThreeCardList {
                                justify-content: space-between;
                                column-gap: 16px;
                                > div {
                                    width: calc(50% - 8px);
                                    &:nth-child(2n) {
                                        [data-test-selector="myLists_PrivateLabel"] {
                                            [class*="TooltipContainer"] {
                                                transform: translateX(10%);
                                                left: auto;
                                                right: 0;
                                            }
                                            [class*="TooltipArrow"] {
                                                transform: initial;
                                                left: auto;
                                                right: 28px;
                                            }
                                        }
                                        .myListsVisibility {
                                            [class*="TooltipContainer"] {
                                                left: -83px;
                                                justify-content: flex-end;
                                            }
                                            [class*="TooltipArrow"] {
                                                right: 4px;
                                                left: auto;
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                        css`
                            column-gap: 24px;
                            row-gap: 40px;
                        `,
                    ],
                    "min",
                )}
            &.moreThanThreeCardList {
                > div {
                    @media screen and (min-width: 768px) and (max-width: 1279px) {
                        width: calc(100% / 2 - 12px);
                        &:nth-child(2n) {
                            [data-test-selector="myLists_PrivateLabel"] {
                                [class*="TooltipContainer"] {
                                    transform: translateX(10%);
                                    left: auto;
                                    right: 0;
                                }
                                [class*="TooltipArrow"] {
                                    transform: initial;
                                    left: auto;
                                    right: 28px;
                                }
                            }
                            .myListsVisibility {
                                [class*="TooltipContainer"] {
                                    left: -83px;
                                    justify-content: flex-end;
                                }
                                [class*="TooltipArrow"] {
                                    right: 4px;
                                    left: auto;
                                }
                            }
                        }
                    }
                    @media screen and (min-width: 1280px) {
                        width: calc(100% / 3 - 16px);
                        &:nth-child(3n) {
                            [data-test-selector="myLists_PrivateLabel"] {
                                [class*="TooltipContainer"] {
                                    transform: translateX(10%);
                                    left: auto;
                                    right: 0;
                                }
                                [class*="TooltipArrow"] {
                                    transform: initial;
                                    left: auto;
                                    right: 28px;
                                }
                            }
                            .myListsVisibility {
                                [class*="TooltipContainer"] {
                                    left: -14px;
                                }
                                [class*="TooltipArrow"] {
                                    right: 70px;
                                    left: auto;
                                }
                            }
                        }
                    }
                    @media screen and (min-width: 1280px) and (max-width: 1400px) {
                        &:nth-child(3n) {
                            .myListsVisibility {
                                [class*="TooltipContainer"] {
                                    left: -83px;
                                    justify-content: flex-end;
                                }
                                [class*="TooltipArrow"] {
                                    right: 4px;
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    createListModal: {
        sizeVariant: "medium",
    },
    buttonWrapper: {
        css: css`
            min-height: 337px;
            border-radius: 8px;
            border: 1px dashed ${colorPallete.grey500};
            background: ${colorPallete.white};
            padding: 40px;
            width: 100%;
            justify-content: center;
            display: flex;
            align-items: center;
        `,
    },
    createNewListButton: {
        css: css`
            font: ${headlineFonts.headline3SemiBoldLg.fontWeight} ${headlineFonts.headline3SemiBoldLg.fontSize} /
                ${headlineFonts.headline3SemiBoldLg.lineHeight} ${headlineFonts.headline3SemiBoldLg.fontFamily};
            color: ${colorPallete.grey800};
            > span {
                display: flex;
                flex-direction: column;
                align-items: center;
                row-gap: 16px;
            }
            svg {
                width: 40px;
                height: 40px;
            }
        `,
    },
};

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            createListModalIsOpen: false,
        };
    }
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    componentDidUpdate() {
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    buttonClickHandler = () => {
        this.setState({ createListModalIsOpen: true });
    };

    modalCloseHandler = () => {
        this.setState({ createListModalIsOpen: false });
    };

    render() {
        if (!this.props.canViewWishLists) {
            return null;
        }

        const {
            recentWishListsDataView: { value: recentWishLists },
            myListsPageNavLink,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        let filterRecentWishLists;
        if (recentWishLists && recentWishLists[0].name == "Recently Purchased") {
            filterRecentWishLists = recentWishLists.slice(0, this.props.fields.numberOfRecords);
        } else {
            filterRecentWishLists = recentWishLists;
        }

        return (
            <StyledWrapper data-test-selector="cardListRecentWishLists">
                <StyledWrapper {...styles.cardTitleWrapper}>
                    <Icon src={MyListIcon} width={40} height={40} />
                    <StyledWrapper>
                        <Typography as="h2" {...styles.myListTitle}>
                            {translate("My Lists")}
                        </Typography>
                        <Link {...styles.viewAllLink} href={myListsPageUrl}>
                            {translate("View all lists")}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper
                    {...styles.cardwrapper}
                    className={
                        filterRecentWishLists && filterRecentWishLists?.length > 2
                            ? "moreThanThreeCardList"
                            : "lessThanThreeCardList"
                    }
                >
                    {filterRecentWishLists &&
                        filterRecentWishLists.map(wishList => (
                            <StyledWrapper key={wishList.id} {...styles.cardList}>
                                <WishListCard
                                    wishList={wishList}
                                    addWishListToCart={this.props.addWishListToCart}
                                    extendedStyles={styles.wishListCard}
                                    toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                    toggleHide={() => this.toggleHideClickHandler(wishList)}
                                />
                            </StyledWrapper>
                        ))}
                    {filterRecentWishLists && filterRecentWishLists.length <= 1 && (
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Link
                                onClick={this.buttonClickHandler}
                                data-test-selector="myLists_createListButton"
                                {...styles.createNewListButton}
                            >
                                {translate("Create a new list")}
                                <TimeToLoad />
                            </Link>
                            <Modal
                                headline={translate("Create New List")}
                                {...styles.createListModal}
                                isOpen={this.state.createListModalIsOpen}
                                handleClose={this.modalCloseHandler}
                            >
                                <MyListsEditListForm
                                    onCancel={this.modalCloseHandler}
                                    onSubmit={this.modalCloseHandler}
                                ></MyListsEditListForm>
                            </Modal>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 2,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
